import React from 'react';

const ConfirmationModal = ({ 
  isOpen, 
  onClose, 
  onConfirm, 
  onCancel,
  title = "Confirm Action",
  message = "Are you sure you want to proceed?",
  confirmText = "Confirm",
  cancelText = "Cancel",
  type = "danger", // 'danger', 'warning', 'info', 'success'
  variant, // Alternative to type
  loading = false,
  disableConfirm = false,
  confirmButtonClass,
  showCommentBox = false,
  comment = "",
  onCommentChange,
  commentPlaceholder = "Add your comment...",
  overlayClassName,
  maxWidth,
  children,
  doubleConfirm = false,
  step1Title,
  step1Message,
  step1ConfirmText = 'Continue',
  finalTitle,
  finalMessage,
  finalConfirmText,
  // Self-contained text input (e.g. a rejection reason): onConfirm(value) receives what was typed
  hasInput = false,
  inputLabel,
  inputRequired = false,
  inputPlaceholder,
}) => {
  const [step, setStep] = React.useState(1);
  const [inputValue, setInputValue] = React.useState('');
  // Callers that only set `showCommentBox` (no `comment` / `onCommentChange`) get a self-managed comment box;
  // before, the box was permanently empty and ignored typing. Callers that control it still do.
  const [innerComment, setInnerComment] = React.useState('');
  const commentControlled = typeof onCommentChange === 'function';
  const commentValue = commentControlled ? comment : innerComment;

  React.useEffect(() => {
    if (isOpen) { setStep(1); setInputValue(''); setInnerComment(''); }
  }, [isOpen]);

  const isFinalStep = !doubleConfirm || step === 2;
  const showComment = showCommentBox && (!doubleConfirm || step === 1);
  const showInput = hasInput && !showCommentBox;
  const inputMissing = showInput && inputRequired && !inputValue.trim();

  React.useEffect(() => {
    if (!isOpen) return undefined;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && !loading) {
        if (onCancel) onCancel();
        else onClose();
      }
      if (e.key === 'Enter' && !loading && !showComment && !showInput && isFinalStep) {
        if (showCommentBox) onConfirm(commentValue);
        else onConfirm();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, loading, showComment, showInput, isFinalStep, showCommentBox, commentValue, onConfirm, onCancel, onClose]);

  if (!isOpen) return null;

  const displayTitle = !doubleConfirm
    ? title
    : (step === 1
      ? (step1Title || title)
      : (finalTitle || `Final confirmation — ${title}`));
  const displayMessage = !doubleConfirm
    ? message
    : (step === 1
      ? (step1Message || message)
      : (finalMessage || (
        typeof message === 'string'
          ? `This is your second confirmation. ${message}`
          : 'This is your second confirmation. Proceed now?'
      )));
  const displayConfirmText = !doubleConfirm
    ? confirmText
    : (step === 1 ? step1ConfirmText : (finalConfirmText || confirmText));

  const getTypeStyles = () => {
    const modalType = variant || type;
    switch (modalType) {
      case 'danger':
        return {
          icon: (
            <svg className="w-6 h-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          ),
          bgColor: 'bg-red-50',
          iconBg: 'bg-red-100',
          buttonBg: 'bg-red-600 hover:bg-red-700 focus:ring-red-500',
          borderColor: 'border-red-200'
        };
      case 'warning':
        return {
          icon: (
            <svg className="w-6 h-6 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.728-.833-2.498 0L4.316 15.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          ),
          bgColor: 'bg-yellow-50',
          iconBg: 'bg-yellow-100',
          buttonBg: 'bg-yellow-600 hover:bg-yellow-700 focus:ring-yellow-500',
          borderColor: 'border-yellow-200'
        };
      case 'success':
        return {
          icon: (
            <svg className="w-6 h-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          ),
          bgColor: 'bg-green-50',
          iconBg: 'bg-green-100',
          buttonBg: 'bg-green-600 hover:bg-green-700 focus:ring-green-500',
          borderColor: 'border-green-200'
        };
      default:
        return {
          icon: (
            <svg className="w-6 h-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          ),
          bgColor: 'bg-blue-50',
          iconBg: 'bg-blue-100',
          buttonBg: 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500',
          borderColor: 'border-blue-200'
        };
    }
  };

  const styles = getTypeStyles();

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget && !loading) {
      handleClose();
    }
  };

  const handleConfirm = () => {
    if (doubleConfirm && step === 1) {
      setStep(2);
      return;
    }
    if (showCommentBox) {
      onConfirm(commentValue);
    } else if (showInput) {
      if (inputMissing) return;
      onConfirm(inputValue.trim());
    } else {
      onConfirm();
    }
  };

  const handleClose = () => {
    setStep(1);
    if (onCancel) {
      onCancel();
    } else {
      onClose();
    }
  };

  return (
    <div 
      className={`fixed inset-0 backdrop-blur-sm bg-black/20 overflow-y-auto h-full w-full ${overlayClassName || 'z-50'} flex items-center justify-center p-4`}
      onClick={handleBackdropClick}
    >
      <div className={`relative w-full ${maxWidth || 'max-w-lg'} bg-white rounded-xl shadow-2xl border border-gray-100 mx-auto animate-in fade-in duration-200`}>
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">{displayTitle}</h3>
          <button
            type="button"
            onClick={handleClose}
            disabled={loading}
            className="w-8 h-8 rounded-lg flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="p-6">
          <div className="flex items-start space-x-4">
            {/* Icon */}
            <div className={`flex-shrink-0 w-12 h-12 rounded-full ${styles.iconBg} flex items-center justify-center`}>
              {styles.icon}
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0">
              <div className="text-sm text-gray-900 mb-3">
                {displayMessage}
              </div>
              {doubleConfirm && (
                <p className="text-[11px] text-gray-500 mb-3">
                  Step {step} of 2
                </p>
              )}

              {/* Item details if provided */}
              {/* {(itemName || itemCode || Object.keys(itemDetails).length > 0) && (
                <div className={`p-3 rounded-lg ${styles.bgColor} ${styles.borderColor} border`}>
                  <div className="text-sm space-y-1">
                    {itemName && (
                      <div className="font-medium text-gray-900">
                        <span className="text-gray-600">Name:</span> {itemName}
                      </div>
                    )}
                    {itemCode && (
                      <div className="font-mono text-xs text-gray-700">
                        <span className="text-gray-600">Code:</span> {itemCode}
                      </div>
                    )}
                    {Object.entries(itemDetails).map(([key, value]) => (
                      <div key={key} className="text-xs text-gray-700">
                        <span className="text-gray-600 capitalize">{key.replace(/([A-Z])/g, ' $1').trim()}:</span> {value}
                      </div>
                    ))}
                  </div>
                </div>
              )} */}

              {children}

              {/* Text input (hasInput) */}
              {showInput && (
                <div className="mt-4">
                  {inputLabel && (
                    <label className="block text-xs font-semibold text-gray-600 mb-1">
                      {inputLabel}{inputRequired && <span className="text-red-500"> *</span>}
                    </label>
                  )}
                  <textarea
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    placeholder={inputPlaceholder || (inputLabel ? `${inputLabel}...` : 'Type here...')}
                    rows={3}
                    autoFocus
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none text-sm"
                  />
                </div>
              )}

              {/* Comment box */}
              {showComment && (
                <div className="mt-4">
                  <textarea
                    value={commentValue}
                    onChange={(e) => (commentControlled ? onCommentChange(e.target.value) : setInnerComment(e.target.value))}
                    placeholder={commentPlaceholder}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none text-sm"
                  />
                </div>
              )}

              {/* {type === 'danger' && showWarning && (
                <div className="mt-3 text-xs text-red-600 font-medium">
                  ⚠️ This action cannot be undone.
                </div>
              )} */}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-4 border-t border-gray-200 bg-gray-50 rounded-b-lg">
          <button
            type="button"
            onClick={handleClose}
            disabled={loading}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {cancelText}
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={loading || disableConfirm || inputMissing}
            className={`px-4 py-2 text-sm font-medium text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${confirmButtonClass || styles.buttonBg} ${loading ? 'cursor-not-allowed' : ''}`}
          >
            {loading && isFinalStep ? (
              <div className="flex items-center space-x-2">
                <svg className="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span>Processing...</span>
              </div>
            ) : (
              displayConfirmText
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmationModal;
